import React, { useState, useCallback, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useForm, Controller } from 'react-hook-form';
import { CustomInput } from '../components/CustomInput';
import { CustomPicker } from '../components/CustomPicker';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import ApiService from '../services/ApiService';
import { Customer, DairyRecord, DairyRecordType, PaymentStatus } from '../models/types';
import { generateId, getISODateString, numericValidation, decimalValidation, parseMilkQuantityToLiters } from '../utils/helpers';

type TransactionType = 'purchase' | 'sale' | 'payment';

interface TransactionFormData {
    date: string;
    recordTime: 'morning' | 'evening';
    quantity: string;
    pricePerUnit: string;
    totalAmount: string;
    amountPaid: string;
    notes: string;
}

const AddCustomerTransactionScreen = ({ navigation, route }: any) => {
    const customer = route.params?.customer as Customer;
    const existingRecord = route.params?.existingRecord as DairyRecord | undefined;
    const defaultType = route.params?.transactionType as TransactionType ||
        (customer.customerType === 'purchase' ? 'purchase' : 'sale');
    const isTypeLocked = !!route.params?.transactionType;

    const isEditMode = !!existingRecord;

    const [loading, setLoading] = useState(false);
    const [transactionType, setTransactionType] = useState<TransactionType>(
        existingRecord?.recordType as TransactionType || defaultType
    );

    // Determine default record time based on current hour
    const getDefaultRecordTime = (): 'morning' | 'evening' => {
        const currentHour = new Date().getHours();
        return currentHour < 12 ? 'morning' : 'evening';
    };

    const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<TransactionFormData>({
        defaultValues: {
            date: existingRecord?.date || getISODateString(new Date()),
            recordTime: existingRecord?.recordTime || getDefaultRecordTime(),
            quantity: existingRecord?.quantity?.toString() || '',
            pricePerUnit: existingRecord?.pricePerUnit?.toString() || '',
            totalAmount: existingRecord?.totalAmount?.toString() || '',
            amountPaid: existingRecord?.amountPaid?.toString() || '',
            notes: existingRecord?.notes || '',
        },
        mode: 'onChange',
    });

    const quantity = watch('quantity');
    const pricePerUnit = watch('pricePerUnit');
    const totalAmount = watch('totalAmount');
    const amountPaid = watch('amountPaid');

    // Auto-calculate total amount when quantity or price changes
    useEffect(() => {
        const qty = parseMilkQuantityToLiters(quantity);
        const price = parseFloat(pricePerUnit) || 0;
        const total = qty * price;
        if (total > 0) {
            setValue('totalAmount', total.toString());
        }
    }, [quantity, pricePerUnit, setValue]);

    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const getPendingAmount = (): number => {
        const total = parseFloat(totalAmount) || 0;
        const paid = parseFloat(amountPaid) || 0;
        return Math.max(0, total - paid);
    };

    const getPaymentStatus = useCallback((): PaymentStatus | undefined => {
        const total = parseFloat(totalAmount) || 0;
        const paid = parseFloat(amountPaid) || 0;

        if (total === 0) return undefined;
        if (paid >= total) return 'paid';
        if (paid > 0) return 'partial';
        return 'pending';
    }, [totalAmount, amountPaid]);

    const onSubmit = useCallback(async (data: TransactionFormData) => {
        setLoading(true);
        try {
            if (transactionType === 'payment') {
                // Handle Payment
                const paymentData = {
                    transactionType: 'payment' as const,
                    amount: parseFloat(data.amountPaid) || 0,
                    date: data.date,
                    notes: data.notes || undefined,
                };

                await ApiService.addCustomerTransaction(customer.id, paymentData);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Payment recorded successfully',
                    onConfirm: () => {
                        setModal(prev => ({ ...prev, visible: false }));
                        navigation.goBack();
                    },
                });
            } else {
                // Handle Dairy Record (Purchase/Sale)
                const totalAmountValue = data.totalAmount ? parseFloat(data.totalAmount) : undefined;
                const amountPaidValue = data.amountPaid ? parseFloat(data.amountPaid) : undefined;
                const pendingAmount = totalAmountValue && amountPaidValue !== undefined ? Math.max(0, totalAmountValue - amountPaidValue) : undefined;

                const qty = parseMilkQuantityToLiters(data.quantity);

                const record: DairyRecord = {
                    id: existingRecord?.id || generateId('DAIRY'),
                    recordType: transactionType as DairyRecordType,
                    date: data.date,
                    recordTime: data.recordTime,
                    quantity: qty,
                    pricePerUnit: data.pricePerUnit ? parseFloat(data.pricePerUnit) : undefined,
                    totalAmount: totalAmountValue,
                    customerName: transactionType === 'sale' ? customer.name : undefined,
                    supplierName: transactionType === 'purchase' ? customer.name : undefined,
                    customerId: customer.id,
                    amountPaid: amountPaidValue,
                    pendingAmount,
                    paymentStatus: getPaymentStatus(),
                    notes: data.notes || undefined,
                    createdAt: existingRecord?.createdAt || new Date().toISOString(),
                };

                if (isEditMode && existingRecord) {
                    await ApiService.updateDairyRecord(existingRecord.id, record);
                    setModal({
                        visible: true,
                        type: 'success',
                        title: 'Success',
                        message: 'Record updated successfully',
                        onConfirm: () => {
                            setModal(prev => ({ ...prev, visible: false }));
                            navigation.goBack();
                        },
                    });
                } else {
                    await ApiService.addDairyRecord(record);
                    setModal({
                        visible: true,
                        type: 'success',
                        title: 'Success',
                        message: `${transactionType.charAt(0).toUpperCase() + transactionType.slice(1)} record added successfully`,
                        onConfirm: () => {
                            setModal(prev => ({ ...prev, visible: false }));
                            navigation.goBack();
                        },
                    });
                }
            }
        } catch (error: any) {
            console.error('Error saving transaction:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: error.message || 'Failed to save. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    }, [transactionType, customer, existingRecord, isEditMode, navigation, getPaymentStatus]);

    const pendingAmount = getPendingAmount();
    const showPaymentFields = parseFloat(totalAmount) > 0;

    const getTypeIcon = (type: TransactionType) => {
        switch (type) {
            case 'purchase': return { name: 'cart-arrow-down', color: '#FF9800' };
            case 'sale': return { name: 'cart-arrow-up', color: '#4CAF50' };
            case 'payment': return { name: 'cash-multiple', color: theme.colors.primary };
        }
    };

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title={isEditMode ? 'Edit Transaction' : 'Add Transaction'}
                showBackButton={true}
                navigation={navigation}
            />

            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                {/* Customer Info Banner */}
                <View style={styles.customerBanner}>
                    <Icon
                        name={customer.customerType === 'sale' ? 'account' : 'account-tie'}
                        size={24}
                        color={theme.colors.primary}
                    />
                    <Text style={styles.customerBannerText}>{customer.name}</Text>
                </View>

                <View style={styles.form}>
                    {/* Transaction Type Selector (Hidden if Edit Mode or Locked) */}
                    {!isEditMode && !isTypeLocked && (
                        <View style={styles.typeSelector}>
                            {(['purchase', 'sale', 'payment'] as TransactionType[]).map(type => {
                                const icon = getTypeIcon(type);
                                const isActive = transactionType === type;
                                return (
                                    <TouchableOpacity
                                        key={type}
                                        style={[
                                            styles.typeButton,
                                            isActive && { backgroundColor: icon.color },
                                        ]}
                                        onPress={() => setTransactionType(type)}
                                    >
                                        <Icon
                                            name={icon.name}
                                            size={18}
                                            color={isActive ? '#fff' : icon.color}
                                        />
                                        <Text style={[
                                            styles.typeButtonText,
                                            isActive && styles.typeButtonText_active,
                                        ]}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                    )}

                    {/* Show locked type indicator if locked */}
                    {!isEditMode && isTypeLocked && (
                        <View style={styles.lockedTypeContainer}>
                            <Text style={styles.sectionTitle}>
                                {transactionType.charAt(0).toUpperCase() + transactionType.slice(1)} Record
                            </Text>
                        </View>
                    )}

                    <Controller
                        control={control}
                        name="date"
                        rules={{ required: 'Date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.date?.message}
                                placeholder="Select date"
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    {/* Transaction Time Picker - Only for Purchase/Sale */}
                    {transactionType !== 'payment' && (
                        <Controller
                            control={control}
                            name="recordTime"
                            render={({ field: { onChange, value } }) => (
                                <CustomPicker
                                    label="Transaction Time *"
                                    value={value}
                                    onValueChange={onChange}
                                    icon="clock-outline"
                                    options={[
                                        { label: 'Morning', value: 'morning' },
                                        { label: 'Evening', value: 'evening' },
                                    ]}
                                />
                            )}
                        />
                    )}

                    {/* Purchase/Sale Form */}
                    {transactionType !== 'payment' ? (
                        <>
                            <Controller
                                control={control}
                                name="quantity"
                                rules={{ required: 'Quantity is required' }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <CustomInput
                                        label="Quantity (L.GGG) *"
                                        value={value}
                                        onChangeText={(text) => onChange(decimalValidation(text))}
                                        onBlur={onBlur}
                                        icon="bottle-soda"
                                        error={errors.quantity?.message}
                                        keyboardType="decimal-pad"
                                        placeholder="e.g., 1.100"
                                    />
                                )}
                            />
                            <Text style={styles.formatHint}>Format: L.GGG (e.g., 1.100 = 1 L 100 g)</Text>

                            <Controller
                                control={control}
                                name="pricePerUnit"
                                rules={{ required: 'Price per unit is required' }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <CustomInput
                                        label="Price Per Unit *"
                                        value={value}
                                        onChangeText={(text) => onChange(decimalValidation(text))}
                                        onBlur={onBlur}
                                        icon="currency-inr"
                                        error={errors.pricePerUnit?.message}
                                        keyboardType="decimal-pad"
                                        placeholder="e.g., 55.50"
                                    />
                                )}
                            />
                            <Text style={styles.formatHint}>Format: number with decimals (e.g., 55.50)</Text>

                            <Controller
                                control={control}
                                name="totalAmount"
                                render={({ field: { value } }) => (
                                    <CustomInput
                                        label="Total Amount"
                                        value={value}
                                        onChangeText={() => {}}
                                        icon="cash-multiple"
                                        keyboardType="number-pad"
                                        placeholder="Auto-calculated"
                                        editable={false}
                                    />
                                )}
                            />
                        </>
                    ) : (
                        <Controller
                            control={control}
                            name="amountPaid"
                            rules={{ required: transactionType === 'payment' ? 'Payment amount is required' : false }}
                            render={({ field: { onChange, onBlur, value } }) => (
                                <CustomInput
                                    label="Payment Amount *"
                                    value={value}
                                    onChangeText={(text) => onChange(numericValidation(text))}
                                    onBlur={onBlur}
                                    icon="cash"
                                    error={errors.amountPaid?.message}
                                    keyboardType="number-pad"
                                    placeholder="Enter amount"
                                />
                            )}
                        />
                    )}

                    {/* Payment Fields */}
                    {showPaymentFields && transactionType !== 'payment' && (
                        <View style={styles.paymentSection}>
                            <Text style={styles.sectionTitle}>Payment Details</Text>

                            <Controller
                                control={control}
                                name="amountPaid"
                                rules={{ required: 'Amount paid is required' }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <CustomInput
                                        label="Amount Paid *"
                                        value={value}
                                        onChangeText={(text) => onChange(numericValidation(text))}
                                        onBlur={onBlur}
                                        icon="cash-check"
                                        error={errors.amountPaid?.message}
                                        keyboardType="number-pad"
                                        placeholder="Amount paid now"
                                    />
                                )}
                            />

                            <View style={styles.pendingRow}>
                                <Text style={styles.pendingLabel}>Pending Amount:</Text>
                                <Text style={[
                                    styles.pendingValue,
                                    pendingAmount > 0 && styles.pendingValueHighlight
                                ]}>
                                    ₹{pendingAmount.toFixed(2)}
                                </Text>
                            </View>

                            {getPaymentStatus() && (
                                <View style={styles.statusRow}>
                                    <Text style={styles.statusLabel}>Payment Status:</Text>
                                    <View style={[
                                        styles.statusBadge,
                                        getPaymentStatus() === 'paid' && styles.paidBadge,
                                        getPaymentStatus() === 'partial' && styles.partialBadge,
                                        getPaymentStatus() === 'pending' && styles.pendingBadge,
                                    ]}>
                                        <Text style={[
                                            styles.statusText,
                                            getPaymentStatus() === 'paid' && styles.paidText,
                                            getPaymentStatus() === 'partial' && styles.partialText,
                                            getPaymentStatus() === 'pending' && styles.pendingTextStyle,
                                        ]}>
                                            {getPaymentStatus()?.toUpperCase()}
                                        </Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    )}

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Notes (Optional)"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={3}
                            />
                        )}
                    />

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.saveBtnText}>
                                {isEditMode ? 'Update' : 'Save'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
            />

            <LoadingOverlay visible={loading} />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.primary,
    },
    backButton: {
        padding: 8,
    },
    headerTitle: {
        ...theme.typography.h5,
        color: '#fff',
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    customerBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.primary + '10',
        padding: theme.spacing.md,
        gap: theme.spacing.sm,
    },
    customerBannerText: {
        ...theme.typography.h6,
        color: theme.colors.primary,
    },
    form: {
        padding: theme.spacing.lg,
    },
    formatHint: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: -theme.spacing.xs,
        marginBottom: theme.spacing.md,
    },
    typeSelector: {
        flexDirection: 'row',
        marginBottom: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.xs,
    },
    typeButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        gap: 4,
    },
    typeButtonText: {
        ...theme.typography.caption,
        color: theme.colors.text,
        fontWeight: '600',
    },
    typeButtonText_active: {
        color: '#fff',
    },
    paymentSection: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    sectionTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        marginBottom: theme.spacing.sm,
    },
    pendingRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    pendingLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
    },
    pendingValue: {
        ...theme.typography.h6,
        color: theme.colors.text,
    },
    pendingValueHighlight: {
        color: theme.colors.error,
    },
    statusRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: theme.spacing.sm,
    },
    statusLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
    },
    statusBadge: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: 4,
        borderRadius: theme.borderRadius.sm,
    },
    paidBadge: {
        backgroundColor: '#E8F5E9',
    },
    partialBadge: {
        backgroundColor: '#FFF3E0',
    },
    pendingBadge: {
        backgroundColor: '#FFEBEE',
    },
    statusText: {
        ...theme.typography.caption,
        fontWeight: '700',
    },
    paidText: {
        color: theme.colors.success,
    },
    partialText: {
        color: theme.colors.warning,
    },
    pendingTextStyle: {
        color: theme.colors.error,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.lg,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
       height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
    lockedTypeContainer: {
        marginBottom: theme.spacing.md,
    },
});

export default AddCustomerTransactionScreen;
